import axios from 'axios';
import { Bot, CheckCircle, Compass, HelpCircle, MessageSquare, Mic, Minimize2, Send, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import * as academicTours from '../utils/adminAcademicTour';
import * as teacherTours from '../utils/teacherAcademicTour';
import './AcademicAssistant.css';

const AcademicAssistant = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const location = useLocation();
    const { userRoles } = useAuth();
    
    const [isOpen, setIsOpen] = useState(false);
    const [command, setCommand] = useState('');
    const [messages, setMessages] = useState([
        { 
            type: 'ai', 
            content: '¡Hola! Soy Yoyo, tu asistente académico. ¿En qué puedo ayudarte hoy? Puedo guiarte para crear aulas, cursos, mallas y más.', 
            timestamp: new Date() 
        }
    ]);
    const [loading, setLoading] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [recognition, setRecognition] = useState(null);

    const [pendingTour, setPendingTour] = useState(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) scrollToBottom();
    }, [messages, isOpen]);

    // Efecto para tours secuenciales (multi-página)
    useEffect(() => {
        if (pendingTour && location.pathname.includes(pendingTour.targetPath)) {
            // Dar tiempo a que el dashboard cargue sus módulos
            const timer = setTimeout(() => {
                executeAction({ action: 'START_TOUR', params: { tour: pendingTour.tour } });
                setPendingTour(null);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [location.pathname, pendingTour]);

    // Speech Recognition Setup
    useEffect(() => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const recognitionInstance = new SpeechRecognition();
            recognitionInstance.continuous = false;
            recognitionInstance.interimResults = true;
            recognitionInstance.lang = 'es-ES';

            recognitionInstance.onresult = (event) => {
                const transcript = Array.from(event.results)
                    .map(result => result[0])
                    .map(result => result.transcript)
                    .join('');
                setCommand(transcript);
            };

            recognitionInstance.onend = () => {
                setIsListening(false);
                if (command.trim().length > 3 && !document.hidden) {
                    handleProcess(command);
                }
            };

            recognitionInstance.onerror = () => setIsListening(false);
            setRecognition(recognitionInstance);
        }
    }, [command]);

    const handleVoiceToggle = () => {
        if (!recognition) return;
        if (isListening) {
            recognition.stop();
        } else {
            setCommand('');
            recognition.start();
            setIsListening(true);
        }
    };

    const handleProcess = async (textToProcess = command) => {
        if (!textToProcess.trim()) return;

        setMessages(prev => [...prev, { type: 'user', content: textToProcess, timestamp: new Date() }]);
        setCommand('');
        setLoading(true);

        try {
            const response = await axios.post('/api/academic-assistant/parse', { 
                command: textToProcess.trim(),
                context: {
                    roles: userRoles.map(r => typeof r === 'string' ? r : r.nombre),
                    currentPath: window.location.pathname
                }
            });

            if (response.data.success) {
                setMessages(prev => [...prev, {
                    type: 'ai',
                    content: response.data.mensaje,
                    actions: response.data.acciones,
                    timestamp: new Date()
                }]);
            } else {
                setMessages(prev => [...prev, {
                    type: 'error',
                    content: 'No pude procesar tu solicitud. Prueba preguntando "¿Cómo creo un aula?"',
                    timestamp: new Date()
                }]);
            }
        } catch (err) {
            setMessages(prev => [...prev, {
                type: 'error',
                content: 'Tuve un problema de conexión. Intenta de nuevo.',
                timestamp: new Date()
            }]);
        } finally {
            setLoading(false);
        }
    };

    const executeAction = (actionObj) => {
        const { action, params } = actionObj;

        if (action === 'NAVIGATE') {
            if (params.path && params.path.includes('[ID]')) {
                // Autocorregir el ID si el usuario ya está incidentalmente dentro de un curso pertinente
                const matchCourseId = window.location.pathname.match(/curso\/(\d+)/) || window.location.pathname.match(/aula\/(\d+)/);
                if (matchCourseId && params.path.startsWith('/docente')) {
                    params.path = params.path.replace('[ID]', matchCourseId[1]);
                } else {
                    const fallbackPath = params.path.startsWith('/docente') ? '/docente/mis-cursos' : '/aulas';
                    const pointerTour = params.path.startsWith('/docente') ? 'POINTER_MIS_CURSOS' : 'AULAS';
                    
                    navigate(fallbackPath);
                    
                    setMessages(prev => [...prev, {
                        type: 'ai',
                        content: 'Para llevarte ahí, primero necesito que entres o selecciones uno de tus cursos en esta pantalla.',
                        actions: [{ label: 'Ver dónde', action: 'START_TOUR', params: { tour: pointerTour } }],
                        timestamp: new Date()
                    }]);

                    // Auto-iniciar el tour del puntero tras la navegación
                    setTimeout(() => {
                        executeAction({ action: 'START_TOUR', params: { tour: pointerTour } });
                    }, 600);

                    // Determinar si debemos encadenar un segundo paso al entrar al curso
                    if (params.path.includes('/configurar-evaluacion')) {
                        setPendingTour({ tour: 'POINTER_PESOS', targetPath: '/docente/curso' });
                    } else if (params.path.includes('/actividades')) {
                        setPendingTour({ tour: 'POINTER_TAREAS', targetPath: '/docente/curso' });
                    } else if (params.path.includes('/unidades') || params.path.includes('/sesiones') || params.path.includes('/academico/aula-docente')) {
                        setPendingTour({ tour: 'POINTER_UNIDADES', targetPath: '/docente/curso' });
                    }
                    
                    return;
                }
            }
            
            navigate(params.path);
            // Si hay un tour asociado, avisar al usuario
            if (params.tour) {
                setMessages(prev => [...prev, {
                    type: 'ai',
                    content: 'Ya estamos aquí. ¿Quieres que inicie la guía interactiva?',
                    actions: [{ label: 'Iniciar Guía', action: 'START_TOUR', params: { tour: params.tour } }],
                    timestamp: new Date()
                }]);
            }
        } else if (action === 'START_TOUR') {
            const tourMap = {
                'GESTION_CURSOS': academicTours.startGestionCursosTour,
                'AULAS': academicTours.startAulasTour,
                'MALLA_CURRICULAR': academicTours.startMallaCurricularTour,
                'UNIDADES': academicTours.startUnidadesTour,
                'COMPETENCIAS': academicTours.startCompetenciasTour,
                'MOVIMIENTO_ALUMNOS': academicTours.startMovimientoTour,
                'REGISTRO_ACTITUD': academicTours.startActitudTour,
                'SABANA_MINEDU': academicTours.startSabanaTour,
                // Teacher Tours
                'DOCENTE_MIS_CURSOS': teacherTours.startDocenteMisCursosTour,
                'PORTAL_DOCENTE': teacherTours.startPortalDocenteTour,
                'DOCENTE_CONFIG_EVAL': teacherTours.startDocenteConfigEvalTour,
                'DOCENTE_SESIONES': teacherTours.startDocenteSesionesTour,
                'DOCENTE_ACTIVIDADES': teacherTours.startDocenteActividadesTour,
                // Punteros Interactivos Dinámicos
                'POINTER_MIS_CURSOS': teacherTours.startPointerMisCursosTour,
                'POINTER_PESOS': teacherTours.startPointerPesosTour,
                'POINTER_TAREAS': teacherTours.startPointerTareasTour,
                'POINTER_UNIDADES': teacherTours.startPointerUnidadesTour
            };

            const tourFunc = tourMap[params.tour];
            if (tourFunc) {
                // Si el tour que estamos iniciando es un puntero intermedio, preparar el siguiente paso
                if (params.tour === 'POINTER_MIS_CURSOS') {
                    // Ya está cubierto en la lógica de NAVIGATE fallback, pero lo guardamos por si acaso
                } else if (params.tour === 'POINTER_PESOS') {
                    setPendingTour({ tour: 'DOCENTE_CONFIG_EVAL', targetPath: '/configurar-evaluacion' });
                } else if (params.tour === 'POINTER_TAREAS') {
                    setPendingTour({ tour: 'DOCENTE_ACTIVIDADES', targetPath: '/actividades' });
                } else if (params.tour === 'POINTER_UNIDADES') {
                    setPendingTour({ tour: 'DOCENTE_SESIONES', targetPath: '/academico/aula-docente' });
                }

                setIsOpen(false); // Cerrar chat para no estorbar el tour
                setTimeout(() => tourFunc(), 500);
            }
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleProcess();
        }
    };

    if (!isOpen) {
        return (
            <div className={`yoyo-assistant-badge ${isDark ? 'dark' : ''}`} onClick={() => setIsOpen(true)}>
                <div className="yoyo-icon">
                    <Bot size={28} />
                    <span className="pulse-ring"></span>
                </div>
                <div className="yoyo-label">
                    <span>YOYO</span>
                    <small>Asistente</small>
                </div>
            </div>
        );
    }

    return (
        <div className={`yoyo-chat-container ${isDark ? 'dark' : ''}`}>
            {/* Header */}
            <div className="yoyo-header">
                <div className="d-flex align-items-center gap-3">
                    <div className="yoyo-avatar">
                        <Bot size={22} />
                    </div>
                    <div>
                        <h6 className="mb-0">Yoyo Académico</h6>
                        <small className="opacity-75">En línea • Guía del Sistema</small>
                    </div>
                </div>
                <div className="d-flex gap-1">
                    <button className="yoyo-btn-icon" onClick={() => setIsOpen(false)}>
                        <Minimize2 size={18} />
                    </button>
                    <button className="yoyo-btn-icon" onClick={() => setIsOpen(false)}>
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="yoyo-body">
                {messages.map((msg, idx) => (
                    <div key={idx} className={`yoyo-msg ${msg.type}`}>
                        <div className="msg-bubble">
                            {msg.content}
                            
                            {msg.actions && msg.actions.length > 0 && (
                                <div className="msg-actions mt-3">
                                    {msg.actions.map((act, aIdx) => (
                                        <Button 
                                            key={aIdx} 
                                            variant="outline-primary" 
                                            size="sm" 
                                            className="w-100 mb-2 text-start d-flex align-items-center justify-content-between"
                                            onClick={() => executeAction(act)}
                                        >
                                            <span>{act.label}</span>
                                            {act.action === 'NAVIGATE' ? <Compass size={14} /> : <HelpCircle size={14} />}
                                        </Button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <span className="msg-time">
                            {msg.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                ))}
                
                {loading && (
                    <div className="yoyo-msg ai">
                        <div className="msg-bubble loading">
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="yoyo-footer">
                <div className="yoyo-input-wrapper">
                    <button 
                        className={`yoyo-mic-btn ${isListening ? 'active' : ''}`}
                        onClick={handleVoiceToggle}
                    >
                        <Mic size={20} />
                    </button>
                    <textarea 
                        placeholder="Pregúntame algo..."
                        rows={1}
                        value={command}
                        onChange={(e) => setCommand(e.target.value)}
                        onKeyDown={handleKeyPress}
                    />
                    <button 
                        className="yoyo-send-btn"
                        onClick={() => handleProcess()}
                        disabled={!command.trim() || loading}
                    >
                        <Send size={20} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AcademicAssistant;
